import { capitalizeFirstLetter, resolveAtDate } from '@flexo/general';
import { navigateToMember, navigateToSite } from '@flexo/resolvers';
import { timeFormat } from 'd3-time-format';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { getAppAccountState } from './appAccountState';

const formatDate = timeFormat('%d/%m/%Y');
const ENV = (import.meta as any).env;

function formatDateTime(value: string | number | Date | null | undefined): string {
  if (!value) return '-';
  const date = new Date(value);
  if (isNaN(date.getTime())) return '-';

  // Source timestamps (disabledAt, lastSignInAt) are UTC-marked; format in UTC
  // so the displayed time matches the actual event (FLEXO-6110: was +2h ahead).
  const dd = String(date.getUTCDate()).padStart(2, '0');
  const mm = String(date.getUTCMonth() + 1).padStart(2, '0');
  const yyyy = date.getUTCFullYear();
  const hh = String(date.getUTCHours()).padStart(2, '0');
  const min = String(date.getUTCMinutes()).padStart(2, '0');
  return `${dd}/${mm}/${yyyy} ${hh}:${min}`;
}

export interface DetailsListItem {
  title: string;
  value: string;
  // Optional secondary text rendered after `value` in deep grey (e.g. the
  // deletion timestamp appended to the "Deleted" state in Last app activity).
  valueSuffix?: string;
}

export interface DetailsListConfig {
  title: string;
  subtitle?: string;
  isClickable?: boolean;
  dataList: DetailsListItem[];
  onTitleClick?: () => void;
  isInactive?: boolean;
  isDocked?: boolean;
}

export type DetailsListType =
  | 'communityPaymentsList'
  | 'memberDetails'
  | 'siteDetails'
  | 'communityDetails'
  | 'siteMembers'
  | 'memberSites'
  | 'MemberDetailsParameters'
  | 'CommunityDetailsParameters';

interface UseDetailsListDataReturn {
  isGroup: boolean;
  expanded: boolean;
  setExpanded: (expanded: boolean) => void;
  singleConfig: DetailsListConfig | null;
  groupConfigs: DetailsListConfig[];
  collapsedPreviewCount: number;
  totalItems: number;
  hasExpandCollapse: boolean;
  shouldScrollGroup: boolean;
}

const RESPONSIVE_GROUP_TYPES: DetailsListType[] = ['siteMembers', 'memberSites'];

/**
 * Custom hook to handle DetailsList data processing and state management
 * @param type - The type of details list to render
 * @returns Object with processed data and utility functions
 */
export function useDetailsListData(
  type: DetailsListType,
  data: any = null
): UseDetailsListDataReturn {
  const { t } = useTranslation();
  const metadata = useSelector((state: any) => state.metadata);
  const calendar = useSelector((state: any) => state.calendar);

  const [expanded, setExpanded] = useState(false);
  const [isViewportSmUp, setIsViewportSmUp] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia('(min-width: 576px)').matches : false
  );
  const [isViewportXlUp, setIsViewportXlUp] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia('(min-width: 1200px)').matches : false
  );

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const smMediaQuery = window.matchMedia('(min-width: 576px)');
    const xlMediaQuery = window.matchMedia('(min-width: 1200px)');
    const handleViewportChange = () => {
      setIsViewportSmUp(smMediaQuery.matches);
      setIsViewportXlUp(xlMediaQuery.matches);
    };

    handleViewportChange();
    smMediaQuery.addEventListener('change', handleViewportChange);
    xlMediaQuery.addEventListener('change', handleViewportChange);

    return () => {
      smMediaQuery.removeEventListener('change', handleViewportChange);
      xlMediaQuery.removeEventListener('change', handleViewportChange);
    };
  }, []);

  // Determine if this is a single item or group
  const isGroup = useMemo(
    () => ['siteMembers', 'memberSites'].includes(type),
    [type]
  );

  // Single configuration creation
  const singleConfig = useMemo(() => {
    if (isGroup) return null;

    const selectedEntity = metadata?.selectedEntity as any;

    switch (type) {
      case 'memberDetails':
        return createMemberDetailsConfig(selectedEntity, data, t, metadata);
      case 'siteDetails':
        return createSiteDetailsConfig(selectedEntity, data, t, calendar);
      case 'communityDetails':
      case 'CommunityDetailsParameters':
        return createCommunityDetailsConfig(selectedEntity, data, t, metadata);
      default:
        throw new Error(`Unknown single type: ${type}`);
    }
  }, [type, isGroup, metadata, t, data, calendar]);

  // Group configurations creation
  const groupConfigs = useMemo(() => {
    if (!isGroup) return [];

    switch (type) {
      case 'siteMembers':
        return createSiteMembersConfigs(metadata, t);
      case 'memberSites':
        return createMemberSitesConfigs(metadata, t);
      default:
        throw new Error(`Unknown group type: ${type}`);
    }
  }, [type, isGroup, metadata, t]);

  const responsiveGroupDisplayMode = useMemo(() => {
    if (!RESPONSIVE_GROUP_TYPES.includes(type)) return 'default';
    if (!isViewportSmUp) return 'toggle';
    if (isViewportXlUp) return 'toggle';
    return 'scroll';
  }, [isViewportSmUp, isViewportXlUp, type]);

  useEffect(() => {
    if (!RESPONSIVE_GROUP_TYPES.includes(type)) return;

    // Reset on mode changes so xs/xl always re-enter in the collapsed state.
    setExpanded(false);
  }, [responsiveGroupDisplayMode, type]);

  const collapsedPreviewCount = useMemo(() => {
    if (!RESPONSIVE_GROUP_TYPES.includes(type)) return 4;
    return responsiveGroupDisplayMode === 'toggle'
      ? 2
      : Number.MAX_SAFE_INTEGER;
  }, [responsiveGroupDisplayMode, type]);

  const totalItems = useMemo(() => groupConfigs?.length || 0, [groupConfigs]);
  const shouldScrollGroup = useMemo(
    () =>
      RESPONSIVE_GROUP_TYPES.includes(type) &&
      responsiveGroupDisplayMode === 'scroll' &&
      totalItems > 2,
    [responsiveGroupDisplayMode, totalItems, type]
  );
  const hasExpandCollapse = useMemo(
    () =>
      RESPONSIVE_GROUP_TYPES.includes(type) &&
      responsiveGroupDisplayMode === 'toggle' &&
      totalItems > 2,
    [responsiveGroupDisplayMode, type, totalItems]
  );

  return {
    isGroup,
    expanded,
    setExpanded,
    singleConfig,
    groupConfigs,
    collapsedPreviewCount,
    totalItems,
    hasExpandCollapse,
    shouldScrollGroup,
  };
}

// Helper functions for creating configurations
function createMemberDetailsConfig(
  selectedEntity: any,
  data: any,
  t: any,
  metadata: any
): DetailsListConfig {
  function getMemberName(member: any) {
    return [member?.memberFirstName, member?.memberLastName].filter(Boolean).join(' ');
  }

  function getMemberSites() {
    return (
      (
        (metadata?.entities as any)?.site?.filter(
          (site: any) => site.memberId === selectedEntity?.memberId
        ) || []
      )?.length || 0
    );
  }

  const titleKey = data?.title || 'widgets.memberDetailsParameters.title';
  const fieldsConfig = (data?.fields || {}) as Record<string, string>;
  const showClerkInfo = ENV.VITE_APP_SHOW_CLERK_MEMBER_INFO !== 'false';
  const clerkInfoFields = ['lastAppActivity', 'disabledAt', 'appAccount'];
  const shownFields: string[] = (Array.isArray(data?.shownFields)
    ? data.shownFields
    : []
  ).filter((field: string) => {
    if (!showClerkInfo && clerkInfoFields.includes(field)) return false;
    // Deleted-at is no longer a separate row: its timestamp is merged into the
    // "Deleted" state of Last app activity (FLEXO-6110).
    if (field === 'disabledAt') return false;
    // App account only makes sense for users that actually have an app account;
    // hide it for unregistered and disabled members (FLEXO-6110).
    if (field === 'appAccount') {
      const state = getAppAccountState(selectedEntity);
      return state === 'registered' || state === 'timestamp';
    }
    return true;
  });

  const getValueForField = (fieldKey: string): string => {
    switch (fieldKey) {
      case 'name': {
        const fullName = `${selectedEntity?.memberFirstName || ''} ${
          selectedEntity?.memberLastName || ''
        }`.trim();
        return fullName ? capitalizeFirstLetter(fullName) : '-';
      }
      case 'startDate':
        return selectedEntity?.startDate
          ? formatDate(new Date(selectedEntity.startDate))
          : '-';
      case 'communityDates': {
        if (!selectedEntity?.startDate) return '-';
        const start = formatDate(new Date(selectedEntity.startDate));
        const endRaw = selectedEntity?.endDate || selectedEntity?.leaveDate;
        if (endRaw) {
          const endDate = new Date(endRaw);
          if (!isNaN(endDate.getTime()) && endDate <= new Date()) {
            return `${start} - ${formatDate(endDate)}`;
          }
        }
        return start;
      }
      case 'memberId':
      case 'clientId':
        return selectedEntity?.memberExternalId || '-';
      case 'sitesNumber':
        return getMemberSites().toString();
      case 'lastAppActivity': {
        switch (getAppAccountState(selectedEntity)) {
          case 'disabled':
            return t('widgets.memberDetailsParameters.disabled');
          case 'timestamp':
            return formatDateTime(selectedEntity.lastSignInAt);
          case 'registered':
            return t('widgets.memberDetailsParameters.registered');
          default:
            return t('widgets.memberDetailsParameters.unregistered');
        }
      }
      case 'disabledAt':
        return formatDateTime(
          selectedEntity?.disabled_at ||
          selectedEntity?.disabledAt ||
          selectedEntity?.lastDisabledAt
        );
      case 'appAccount':
        // Only the Clerk account email — never fall back to the contact email
        return (
          selectedEntity?.appAccount ||
          selectedEntity?.appAccountEmail ||
          selectedEntity?.clerkEmail ||
          selectedEntity?.userEmail ||
          selectedEntity?.primaryEmail ||
          selectedEntity?.primaryEmailAddress?.emailAddress ||
          '-'
        );
      default: {
        const value = selectedEntity?.[fieldKey as keyof typeof selectedEntity];
        return value === undefined || value === null || value === ''
          ? '-'
          : String(value);
      }
    }
  };

  // Deep-grey suffix appended after the value. Used to show the deletion
  // timestamp after the "Deleted" state in Last app activity (FLEXO-6110).
  const getValueSuffixForField = (fieldKey: string): string | undefined => {
    if (
      fieldKey === 'lastAppActivity' &&
      getAppAccountState(selectedEntity) === 'disabled'
    ) {
      const ts = formatDateTime(
        selectedEntity?.disabled_at ||
          selectedEntity?.disabledAt ||
          selectedEntity?.lastDisabledAt
      );
      return ts !== '-' ? `- ${ts}` : undefined;
    }
    return undefined;
  };

  const dataList: DetailsListItem[] = [
    ...shownFields.map((fieldKey) => ({
      title: t(fieldsConfig?.[fieldKey] || fieldKey),
      value: getValueForField(fieldKey),
      valueSuffix: getValueSuffixForField(fieldKey),
    })),
    // Optional trailing metric from config
    ...(data?.sitesNumber
      ? [{ title: t(data.sitesNumber), value: getMemberSites().toString() }]
      : []),
  ];

  return {
    title: t(titleKey),
    subtitle: capitalizeFirstLetter(getMemberName(selectedEntity)),
    isClickable: false,
    dataList,
  };
}

function createSiteDetailsConfig(
  selectedEntity: any,
  data: any,
  t: any,
  calendar: any
): DetailsListConfig {
  const titleKey = data?.title || 'widgets.siteDetailsParameters.title';

  // Support two shapes:
  // 1) New: data.fields + data.shownFields
  // 2) Legacy: data holds label keys directly (siteName, joinDate, ownership, type, productionBenefits)
  const fieldsConfig: Record<string, string> = data?.fields
    ? (data.fields as Record<string, string>)
    : ((): Record<string, string> => {
        const { title, ...rest } = data || {};
        return (rest || {}) as Record<string, string>;
      })();

  const shownFields: string[] = Array.isArray(data?.shownFields)
    ? (data.shownFields as string[])
    : Object.keys(fieldsConfig);

  const getValueForField = (fieldKey: string): string => {
    switch (fieldKey) {
      case 'siteName':
        return selectedEntity?.siteName || '-';
      case 'externalId':
        return selectedEntity?.siteExternalId || '-';
      case 'joinDate': {
        const calDateFrom = calendar?.hiveCalendar?.selectedDate?.[0];
        const refDate = calDateFrom
          ? new Date(calDateFrom).toISOString().slice(0, 10)
          : null;

        const changelogEntry = refDate
          ? resolveAtDate(selectedEntity?.changelog, refDate)
          : null;

        const startDate = changelogEntry
          ? changelogEntry.startDate
          : selectedEntity?.joinDate || selectedEntity?.startDate;
        const endDateRaw = changelogEntry
          ? changelogEntry.endDate
          : selectedEntity?.leaveDate;

        const formattedStart = startDate ? formatDate(new Date(startDate)) : '';
        const formattedEnd =
          endDateRaw && new Date(endDateRaw) <= new Date()
            ? ` - ${formatDate(new Date(endDateRaw))}`
            : '';
        const result = `${formattedStart}${formattedEnd}`;
        return result || '-';
      }
      case 'type': {
        const typeKey = selectedEntity?.siteType?.[0]?.siteType || 'default';
        return t(`widgets.siteDetailsParameters.${typeKey}`);
      }
      default: {
        const value = selectedEntity?.[fieldKey as keyof typeof selectedEntity];
        return value === undefined || value === null || value === ''
          ? '-'
          : String(value);
      }
    }
  };

  const dataList: DetailsListItem[] = shownFields.map((fieldKey) => ({
    title: t(fieldsConfig?.[fieldKey] || fieldKey),
    value: getValueForField(fieldKey),
  }));

  return {
    title: t(titleKey),
    subtitle: selectedEntity?.siteName || selectedEntity?.pod || '',
    isClickable: false,
    dataList,
  };
}

function createCommunityDetailsConfig(
  selectedEntity: any,
  data: any,
  t: any,
  metadata: any
): DetailsListConfig {
  const titleKey = data?.title || 'widgets.communityDetailsParameters.title';
  const fieldsConfig = (data?.fields || {}) as Record<string, string>;
  const shownFields: string[] = Array.isArray(data?.shownFields)
    ? data.shownFields
    : [];

  const getValueForField = (fieldKey: string): string => {
    switch (fieldKey) {
      case 'startDate':
        return selectedEntity?.startDate
          ? formatDate(new Date(selectedEntity.startDate))
          : '-';
      case 'communityDates': {
        if (!selectedEntity?.startDate) return '-';
        const start = formatDate(new Date(selectedEntity.startDate));
        let endRaw = selectedEntity?.endDate || selectedEntity?.leaveDate;
        if (!endRaw) {
          const fromEntities = (metadata?.entities?.community || metadata?.entities?.communities || [])
            .find((c: any) => c.communityId === selectedEntity?.communityId);
          endRaw = fromEntities?.endDate || fromEntities?.leaveDate;
        }
        if (endRaw) {
          const endDate = new Date(endRaw);
          if (!isNaN(endDate.getTime()) && endDate <= new Date()) {
            return `${start} - ${formatDate(endDate)}`;
          }
        }
        return start;
      }
      case 'members':
        return (
          (
            metadata?.entities?.member?.filter(
              (member: any) =>
                member?.communityId === selectedEntity?.communityId
            ) || []
          )?.length || 0
        );
      default: {
        const value = selectedEntity?.[fieldKey as keyof typeof selectedEntity];
        return value === undefined || value === null || value === ''
          ? '-'
          : String(value);
      }
    }
  };

  const dataList: DetailsListItem[] = shownFields.map((fieldKey) => ({
    title: t(fieldsConfig?.[fieldKey] || fieldKey),
    value: getValueForField(fieldKey),
  }));

  return {
    title: t(titleKey),
    isClickable: false,
    dataList,
  };
}

function createSiteMembersConfigs(metadata: any, t: any): DetailsListConfig[] {
  const membersList = [
    ...((metadata?.sortedEntities as any)?.communities || []).reduce(
      (prev: any, next: any) => [...prev, ...(next?.members || [])],
      []
    ),
    ...((metadata as any)?.unassignedMembers || []),
  ]?.filter((member: any) =>
    member?.sites?.some(
      (site: any) => site?.siteId === metadata?.selectedEntityId
    )
  );

  const { since, client_id, billing_address, phone, email } = t(
    'widgets.siteMembersList',
    { returnObjects: true }
  ) as any;

  const relations = membersList.flatMap((member: any) =>
    (member?.sites || [])
      .filter((site: any) => site?.siteId === metadata?.selectedEntityId)
      .map((site: any) => {
        const startDate = site?.startDate || site?.joinDate || null;
        const endDate = site?.endDate || site?.leaveDate || null;

        return {
          member,
          startDate,
          endDate,
          dedupeKey: [
            member?.memberId || member?.memberExternalId || '',
            startDate || '',
            endDate || '',
          ].join('::'),
        };
      })
  );

  const seenKeys = new Set<string>();
  const dedupedRelations = relations.filter((relation: any) => {
    if (seenKeys.has(relation.dedupeKey)) return false;
    seenKeys.add(relation.dedupeKey);
    return true;
  });

  return dedupedRelations.map(({ member, startDate, endDate }: any) => ({
      title: capitalizeFirstLetter(
        [member?.memberFirstName, member?.memberLastName].filter(Boolean).join(' ')
      ),
      isClickable: true,
      onTitleClick: () => navigateToMember('member', member?.memberId),
      dataList: [
        {
          title: since,
          value: formatRelationDates(startDate, endDate),
        },
        {
          title: client_id,
          value: member?.memberExternalId || '-',
        },
        {
          title: billing_address,
          value:
            !member?.address || !member?.city
              ? '-'
              : `${member?.address}, ${member?.city}`,
        },
        {
          title: phone,
          value: member?.phone || '-',
        },
        {
          title: email,
          value: member?.email || '-',
        },
      ],
    })
  );
}

function formatRelationDates(
  startDateRaw?: string | null,
  endDateRaw?: string | null
) {
  if (!startDateRaw) return '-';

  const startDate = new Date(startDateRaw);
  if (isNaN(startDate.getTime())) return '-';

  const formattedStartDate = formatDate(startDate);
  if (!endDateRaw) return formattedStartDate;

  const endDate = new Date(endDateRaw);
  if (isNaN(endDate.getTime()) || endDate > new Date()) {
    return formattedStartDate;
  }

  return `${formattedStartDate} - ${formatDate(endDate)}`;
}

function createMemberSitesConfigs(metadata: any, t: any): DetailsListConfig[] {
  const sites = [...((metadata?.selectedEntity as any)?.sites || [])].sort(
    (a: any, b: any) => {
      const endA = a.endDate || a.leaveDate;
      const endB = b.endDate || b.leaveDate;
      const now = new Date();
      const isExpiredA = endA && new Date(endA) < now;
      const isExpiredB = endB && new Date(endB) < now;

      if (isExpiredA && !isExpiredB) return 1;
      if (!isExpiredA && isExpiredB) return -1;

      if (!endA && !endB) return 0;
      if (!endA) return -1;
      if (!endB) return 1;
      return new Date(endB).getTime() - new Date(endA).getTime();
    }
  );

  return sites.map((site: any) => {
    const endDate = site.endDate || site.leaveDate;
    const startDate = site.startDate || site.joinDate;
    const address = site.address || site.siteAddress;
    const city = site.city || '';
    const siteType = site.type || site.siteType?.[0]?.siteType;
    const isInactive = endDate && new Date(endDate) < new Date();

    return {
      title: capitalizeFirstLetter(site.siteName),
      subtitle: site.siteExternalId,
      isClickable: true,
      isInactive,
      onTitleClick: () => navigateToSite('site', site?.siteId),
      dataList: [
        {
          title: t('widgets.memberSitesDetails.address'),
          value: address && city ? `${address}, ${city}` : address || '-',
        },
        {
          title: t('widgets.memberSitesDetails.owned'),
          value: `${startDate ? formatDate(new Date(startDate)) : '-'}${
            isInactive ? ` - ${formatDate(new Date(endDate))}` : ''
          }`,
        },
        {
          title: t('widgets.memberSitesDetails.type'),
          value: capitalizeFirstLetter(siteType),
        },
      ],
    };
  });
}

export default useDetailsListData;
